import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Home = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 to-black z-0"></div>
        
        <div className="container mx-auto relative z-10 text-center">
          <div className="mb-8 animate-fade-in relative">
            <div className="absolute inset-0 bg-gradient-to-b from-blue-500/20 to-transparent blur-3xl"></div>
            <img 
              src="https://customer-assets.emergentagent.com/job_car-detailing-9/artifacts/0t37y2eh_IMG_4269.jpeg" 
              alt="AutoClean Logo" 
              className="w-64 h-64 mx-auto object-contain relative z-10 drop-shadow-2xl brightness-110 contrast-125"
              style={{filter: 'drop-shadow(0 0 40px rgba(14, 165, 233, 0.3))'}}
            />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-up">
            <span className="text-white">AutoClean</span>
          </h1>
          
          <p className="text-2xl md:text-3xl mb-12 text-blue-400 font-light animate-slide-up" style={{animationDelay: '0.2s'}}>
            Clean d'un clin d'œil
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{animationDelay: '0.6s'}}>
            <Link to="/services">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/50">
                <Sparkles className="mr-2 h-5 w-5" />
                Découvrir nos formules
              </Button>
            </Link>
            <a href="tel:0766845519">
              <Button className="bg-white hover:bg-gray-100 text-black px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl">
                <Phone className="mr-2 h-5 w-5" />
                Nous contacter
              </Button>
            </a>
          </div>
        </div>
        
        {/* Animated background elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-blue-600/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-950">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              Votre spécialiste en <span className="text-blue-500">nettoyage automobile</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-12">
              AutoClean est votre partenaire de confiance à Athis-Mons pour redonner à votre véhicule 
              tout son éclat. Nous proposons des services de nettoyage intérieur et extérieur de qualité 
              professionnelle, adaptés à tous vos besoins.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <Card className="bg-gray-900/50 border-blue-500/30 p-6 hover:bg-gray-900/70 transition-all duration-300 hover:scale-105">
                <div className="text-blue-500 mb-4 flex justify-center">
                  <Sparkles className="h-12 w-12" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">Qualité Premium</h3>
                <p className="text-gray-400">Des prestations haut de gamme pour un résultat impeccable</p>
              </Card>
              
              <Card className="bg-gray-900/50 border-blue-500/30 p-6 hover:bg-gray-900/70 transition-all duration-300 hover:scale-105">
                <div className="text-blue-500 mb-4 flex justify-center">
                  <Phone className="h-12 w-12" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">Sur Rendez-vous</h3>
                <p className="text-gray-400">Service personnalisé selon vos disponibilités</p>
              </Card>
              
              <Card className="bg-gray-900/50 border-blue-500/30 p-6 hover:bg-gray-900/70 transition-all duration-300 hover:scale-105">
                <div className="text-blue-500 mb-4 flex justify-center">
                  <MapPin className="h-12 w-12" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">Proximité</h3>
                <p className="text-gray-400">Situé à Athis-Mons, facilement accessible</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-950">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            Pourquoi <span className="text-blue-500">AutoClean</span> ?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-gray-900/50 border-blue-500/20 p-8 hover:border-blue-500/40 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-500/20 p-3 rounded-lg">
                  <Sparkles className="h-8 w-8 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Résultats Garantis</h3>
                  <p className="text-gray-400">Satisfaction client à 100%. Nous ne nous arrêtons que lorsque vous êtes pleinement satisfait du résultat.</p>
                </div>
              </div>
            </Card>
            
            <Card className="bg-gray-900/50 border-blue-500/20 p-8 hover:border-blue-500/40 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-500/20 p-3 rounded-lg">
                  <Phone className="h-8 w-8 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Rendez-vous Flexible</h3>
                  <p className="text-gray-400">Service sur rendez-vous adapté à votre emploi du temps. Nous nous ajustons à vos disponibilités.</p>
                </div>
              </div>
            </Card>
            
            <Card className="bg-gray-900/50 border-blue-500/20 p-8 hover:border-blue-500/40 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-500/20 p-3 rounded-lg">
                  <Sparkles className="h-8 w-8 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Produits Professionnels</h3>
                  <p className="text-gray-400">Utilisation exclusive de produits de qualité professionnelle pour préserver et embellir votre véhicule.</p>
                </div>
              </div>
            </Card>
            
            <Card className="bg-gray-900/50 border-blue-500/20 p-8 hover:border-blue-500/40 transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-500/20 p-3 rounded-lg">
                  <MapPin className="h-8 w-8 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Proximité & Accessibilité</h3>
                  <p className="text-gray-400">Situé à Athis-Mons, facilement accessible pour vous offrir un service de proximité rapide.</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-gray-950">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-6">
            Ce que disent nos <span className="text-blue-500">clients</span>
          </h2>
          <p className="text-center text-gray-300 mb-12 text-lg">
            Plus de 500 clients satisfaits nous font confiance
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gray-900/50 border-blue-500/30 p-6">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-2xl">★★★★★</div>
              </div>
              <p className="text-gray-300 mb-4 italic">"Nettoyage au top ! J'ai pris intérieur et extérieur, on aurait dit qu'elle était neuve. L'équipe est très sympa, on m'avait dit que cela prendrait 2h et en 1h30 c'était fini. Je recommande vivement, le prix est très abordable !"</p>
              <p className="text-blue-400 font-semibold">- Gautier A.</p>
            </Card>
            
            <Card className="bg-gray-900/50 border-blue-500/30 p-6">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-2xl">★★★★★</div>
              </div>
              <p className="text-gray-300 mb-4 italic">"Franchement, super expérience chez AutoClean ! J'ai laissé ma voiture là-bas et à mon retour elle était impeccable. Nettoyage nickel, intérieur comme extérieur, rien à redire. On sent qu'ils prennent leur travail au sérieux. Je recommande sans hésiter !"</p>
              <p className="text-blue-400 font-semibold">- L.O.</p>
            </Card>
            
            <Card className="bg-gray-900/50 border-blue-500/30 p-6">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-2xl">★★★★★</div>
              </div>
              <p className="text-gray-300 mb-4 italic">"Très bonne expérience. L'équipe est discrète et efficace. Ma voiture a été nettoyée comme neuve, quel plaisir de la revoir dans cet état ! Ils proposent une salle d'attente si besoin. J'ai pris le pack complet pour une durée de 2h. Merci à l'équipe !"</p>
              <p className="text-blue-400 font-semibold">- Jérôme P.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Final Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-950 to-black">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Votre voiture mérite le <span className="text-blue-500">meilleur</span>
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Offrez à votre véhicule le traitement qu'il mérite. Prenez rendez-vous dès aujourd'hui et découvrez la différence AutoClean.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a href="tel:0766845519">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/50">
                <Phone className="mr-2 h-6 w-6" />
                07 66 84 55 19
              </Button>
            </a>
            <Link to="/services">
              <Button className="bg-white hover:bg-gray-100 text-black px-10 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl">
                Découvrir nos formules
              </Button>
            </Link>
          </div>
          
          <p className="text-gray-400 text-sm">
            Rendez-vous uniquement • Paiement sur place • Athis-Mons
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;